import { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Edit2, Printer } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import QRCodeCanvas from 'react-qr-code';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { DataTable } from '../components/ui/data-table';
import { displayQrCodeStatusLabel, qrStatusBadgeClassName } from '../utils/qrCodeStatusLabel';
import { generateQRLabelPdf, QRLabelItem } from '../utils/qrLabelPdfGenerator';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface QRCodeData {
  id: string;
  code: string;
  status: string;
  asset_instance_id: string | null;
  asset_instance?: {
    id: string;
    instance_number: string;
    template: {
      id: string;
      name: string;
      images?: Array<{
        id: string;
        url?: string;
        variant?: string;
      }>;
    };
  };
  scan_count: number;
  generated_at: string;
  assigned_at: string | null;
  last_scanned_at: string | null;
  assigned_by?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface ScanEvent {
  id: string;
  scan_method: string;
  scan_context: string;
  workflow_stage?: string | null;
  scanned_by?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  location?: {
    id: string;
    name: string;
  };
  created_at: string;
  device_info?: Record<string, any>;
  task?: { id: string; task_number?: string; task_type?: string } | null;
  job?: { id: string; job_number?: string } | null;
  shipment?: { id: string; bl_number?: string } | null;
}

const SCAN_BASE_URL = process.env.REACT_APP_SCAN_BASE_URL || 'https://hub.homebound.co.uk';

export default function QRCodeDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [qrCode, setQrCode] = useState<QRCodeData | null>(null);
  const [scanEvents, setScanEvents] = useState<ScanEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  useEffect(() => {
    loadQRCode();
    loadScanHistory();
  }, [id]);

  const loadQRCode = async () => {
    try {
      const response = await fetch(`/api/qr-codes/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setQrCode(data.data);
      }
    } catch (error) {
      console.error('Error loading QR code:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadScanHistory = async () => {
    try {
      const response = await fetch(`/api/scan/history/code/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setScanEvents(data.data);
      }
    } catch (error) {
      console.error('Error loading scan history:', error);
    }
  };

  const columns: ColumnDef<ScanEvent>[] = useMemo(() => [
    {
      accessorKey: 'created_at',
      header: 'Timestamp',
      cell: ({ row }) => (
        <div className="text-sm">
          {new Date(row.original.created_at).toLocaleString()}
        </div>
      ),
    },
    {
      accessorKey: 'scan_method',
      header: 'Method',
      cell: ({ row }) => (
        <Badge variant={row.original.scan_method === 'qr' ? 'default' : 'secondary'}>
          {row.original.scan_method.toUpperCase()}
        </Badge>
      ),
    },
    {
      accessorKey: 'scan_context',
      header: 'Context',
      cell: ({ row }) => (
        <Badge variant="outline">
          {row.original.scan_context.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      id: 'workflow_stage',
      header: 'Stage',
      cell: ({ row }) => {
        const s = row.original.workflow_stage;
        return s ? <Badge variant="secondary">{s}</Badge> : <span className="text-muted-foreground">-</span>;
      },
    },
    {
      id: 'user',
      header: 'User',
      cell: ({ row }) => {
        const user = row.original.scanned_by;
        if (!user) return <span className="text-muted-foreground">Anonymous</span>;
        const displayName = user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : user.email;
        return <div className="text-sm">{displayName}</div>;
      },
    },
    {
      id: 'location',
      header: 'Location',
      cell: ({ row }) => {
        const location = row.original.location;
        if (!location) return <span className="text-muted-foreground">-</span>;
        return <div className="text-sm">{location.name}</div>;
      },
    },
    {
      id: 'device',
      header: 'Device',
      cell: ({ row }) => {
        const device = row.original.device_info;
        if (!device) return <span className="text-muted-foreground">-</span>;
        return (
          <div className="text-sm">
            {device.type || 'Unknown'}
          </div>
        );
      },
    },
    {
      id: 'task',
      header: 'Task',
      cell: ({ row }) => {
        const t = row.original.task;
        if (!t) return <span className="text-muted-foreground">-</span>;
        return (
          <button
            type="button"
            onClick={() => navigate(`/tasks/${t.id}`)}
            className="text-primary hover:underline text-sm"
          >
            {t.task_number || t.id}
          </button>
        );
      },
    },
    {
      id: 'job',
      header: 'Job',
      cell: ({ row }) => {
        const j = row.original.job;
        if (!j) return <span className="text-muted-foreground">-</span>;
        return (
          <button
            type="button"
            onClick={() => navigate(`/jobs/${j.id}`)}
            className="text-primary hover:underline text-sm"
          >
            {j.job_number || j.id}
          </button>
        );
      },
    },
    {
      id: 'shipment',
      header: 'Shipment',
      cell: ({ row }) => {
        const s = row.original.shipment;
        if (!s) return <span className="text-muted-foreground">-</span>;
        return (
          <button
            type="button"
            onClick={() => navigate(`/shipments/${s.id}`)}
            className="text-primary hover:underline text-sm"
          >
            {s.bl_number || s.id}
          </button>
        );
      },
    },
  ], [navigate]);

  const url = qrCode ? `${SCAN_BASE_URL}?q=${qrCode.code}` : '';

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="qr-codes" />

      <main className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : !qrCode ? (
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold mb-2">QR Code not found</h2>
            <p className="text-muted-foreground mb-4">The requested QR code does not exist.</p>
            <Button onClick={() => navigate('/qr-codes')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to QR Codes
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-6 flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/qr-codes')} className="h-8 w-8 shrink-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-2xl font-bold truncate">QR Code: {qrCode.code}</h1>
              <div className="flex gap-2 ml-auto shrink-0">
                {qrCode.asset_instance && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const instance = qrCode.asset_instance!;
                      const imageUrl = instance.template?.images?.[0]?.url ?? undefined;
                      const item: QRLabelItem = {
                        qrCodeValue: url,
                        qrCodeText: qrCode.code,
                        productName: instance.template?.name || 'Unknown Product',
                        unitNumber: instance.instance_number,
                        imageUrl,
                      };
                      generateQRLabelPdf([item], { filename: `qr-label-${qrCode.code}.pdf` });
                    }}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print Label
                  </Button>
                )}
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/qr-codes/${id}/assign`}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    {qrCode.asset_instance_id ? 'Reassign' : 'Assign'}
                  </Link>
                </Button>
              </div>
            </div>

      <div className="grid gap-6 md:grid-cols-2 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Code Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6">
              <div className="flex-1 space-y-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Code</div>
                  <div className="text-lg font-mono font-bold">{qrCode.code}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">URL</div>
                  <div className="text-sm break-all">{url}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Status</div>
                  <Badge className={qrStatusBadgeClassName[displayQrCodeStatusLabel(qrCode)] ?? 'bg-gray-500'}>
                    {displayQrCodeStatusLabel(qrCode)}
                  </Badge>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Scan Count</div>
                  <div className="text-lg font-semibold">{qrCode.scan_count}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Generated</div>
                  <div className="text-sm">
                    {new Date(qrCode.generated_at).toLocaleString()}
                  </div>
                </div>
                {qrCode.last_scanned_at && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Last Scanned</div>
                    <div className="text-sm">
                      {new Date(qrCode.last_scanned_at).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                <div className="p-4 bg-white rounded-lg border">
                  <QRCodeCanvas value={url} size={180} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Assignment</CardTitle>
          </CardHeader>
          <CardContent>
            {qrCode.asset_instance ? (
              <div className="flex gap-6">
                <div className="flex-1 space-y-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Asset Instance</div>
                    <Link
                      to={`/units/${qrCode.asset_instance.id}`}
                      className="text-primary hover:underline font-medium"
                    >
                      {qrCode.asset_instance.template.name} ({qrCode.asset_instance.instance_number})
                    </Link>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Assigned At</div>
                    <div className="text-sm">
                      {qrCode.assigned_at
                        ? new Date(qrCode.assigned_at).toLocaleString()
                        : '-'}
                    </div>
                  </div>
                  {qrCode.assigned_by && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Assigned By</div>
                      <div className="text-sm">
                        {qrCode.assigned_by.first_name && qrCode.assigned_by.last_name
                          ? `${qrCode.assigned_by.first_name} ${qrCode.assigned_by.last_name}`
                          : qrCode.assigned_by.email}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {qrCode.asset_instance.template.images && qrCode.asset_instance.template.images.length > 0 ? (
                    <Link
                      to={`/units/${qrCode.asset_instance.id}`}
                      className="block"
                    >
                      <div className="w-[212px] h-[212px] rounded-lg border bg-white p-4 hover:shadow-lg transition-shadow duration-200">
                        <img
                          src={qrCode.asset_instance.template.images[0].url || `/api/asset-templates/images/${qrCode.asset_instance.template.images[0].id}/url`}
                          alt={qrCode.asset_instance.template.name}
                          className="w-full h-full object-contain cursor-pointer"
                        />
                      </div>
                    </Link>
                  ) : (
                    <div className="w-[212px] h-[212px] rounded-lg border bg-gray-50 flex items-center justify-center">
                      <div className="text-center text-muted-foreground">
                        <div className="text-4xl mb-2">📦</div>
                        <div className="text-sm">No image available</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-muted-foreground">
                Not assigned to any asset instance
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Scan History</CardTitle>
          <CardDescription>
            All scan events for this QR code
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={scanEvents}
            searchKey="scan_method"
            searchPlaceholder="Search scans..."
            emptyMessage="No scans recorded yet"
          />
        </CardContent>
      </Card>
          </>
        )}
      </main>
    </div>
  );
}

